import { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { parseMeasurement } from '../../../utils/measurements.js';

const db = getFirestore();

export const useRecipeForm = ({ recipe, user, inventory, onIngredientAdded, onRecipeAdded, onRecipeUpdated, navigate }) => {
  const isEditMode = !!recipe;
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [amountErrors, setAmountErrors] = useState({});

  // Initialize recipeData based on whether we're editing or creating new
  const [recipeData, setRecipeData] = useState(() => {
    if (recipe) {
      const formattedIngredients = recipe.ingredients.map(ing => {
        let amount = '1';
        let unit = 'oz';

        if (ing.measurement) {
          amount = ing.measurement.value.toString();
          unit = ing.measurement.unit;
        } else if (ing.amount) {
          const parts = ing.amount.split(' ');
          if (parts.length === 2) {
            amount = parts[0];
            unit = parts[1];
          }
        }

        return {
          name: ing.name || '',
          amount: amount,
          unit: unit,
          ingredientId: ing.ingredientId || ''
        };
      });

      return {
        name: recipe.name || '',
        origin: 'My Recipes',
        description: recipe.description || '',
        ingredients: formattedIngredients.length > 0
          ? formattedIngredients
          : [{ name: '', amount: '1', unit: 'oz', ingredientId: '' }],
        instructions: recipe.instructions || '',
        garnish: recipe.garnish || '',
        glass: recipe.glass || recipe.glassware || ''
      };
    }

    // Default new recipe
    return {
      name: '',
      origin: 'My Recipes',
      description: '',
      ingredients: [{ name: '', amount: '1', unit: 'oz', ingredientId: '' }],
      instructions: '',
      garnish: '',
      glass: ''
    };
  });

  useEffect(() => {
    if (recipe) {
      setRecipeData({
        name: recipe.name || '',
        origin: 'My Recipes',
        description: recipe.description || '',
        ingredients: recipe.ingredients.map(ing => ({
          name: ing.name || '',
          amount: ing.amount?.split(' ')[0] || '1',
          unit: ing.amount?.split(' ')[1] || 'oz',
          ingredientId: ing.ingredientId || ''
        })),
        instructions: recipe.instructions || '',
        garnish: recipe.garnish || '',
        glass: recipe.glass || recipe.glassware || ''
      });
    }
  }, [recipe]);

  const parseAmountInput = (input) => {
    if (input === null || input === undefined) {
      return null;
    }

    const inputStr = String(input);
    const trimmedInput = inputStr.trim();

    if (trimmedInput === '') {
      return null;
    }

    // Handle special Unicode fraction characters
    if (/[½¼¾⅓⅔]/.test(trimmedInput)) {
      const normalizedInput = trimmedInput
        .replace(/½/g, '0.5')
        .replace(/¼/g, '0.25')
        .replace(/¾/g, '0.75')
        .replace(/⅓/g, '0.33')
        .replace(/⅔/g, '0.67');
      return parseFloat(normalizedInput).toString();
    }

    // Handle fractions like "1/2"
    if (trimmedInput.includes('/')) {
      const [numerator, denominator] = trimmedInput.split('/').map(num => parseFloat(num.trim()));
      if (denominator === 0 || isNaN(numerator) || isNaN(denominator)) {
        return null;
      }
      return (numerator / denominator).toString();
    }

    const numericValue = parseFloat(trimmedInput);
    if (isNaN(numericValue)) {
      return null;
    }

    return numericValue.toString();
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...recipeData.ingredients];
    newIngredients[index] = {
      ...newIngredients[index],
      [field]: value
    };

    if (field === 'name') {
      const existingIngredient = inventory.find(i =>
        i.name.toLowerCase() === value.toLowerCase()
      );
      if (existingIngredient) {
        newIngredients[index].ingredientId = existingIngredient.id;
      } else {
        newIngredients[index].ingredientId = '';
      }
    }

    setRecipeData({ ...recipeData, ingredients: newIngredients });
  };

  const handleAmountChange = (index, inputValue) => {
    const stringValue = inputValue !== undefined ? String(inputValue) : '';
    const trimmedValue = stringValue.trim();
    const newIngredients = [...recipeData.ingredients];

    if (trimmedValue === '') {
      setAmountErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      newIngredients[index] = {
        ...newIngredients[index],
        amount: ''
      };
      setRecipeData({ ...recipeData, ingredients: newIngredients });
      return;
    }

    const parsedValue = parseAmountInput(trimmedValue);
    if (parsedValue === null) {
      setAmountErrors(prev => ({
        ...prev,
        [index]: 'Invalid amount. Use whole numbers, decimals, or fractions (e.g., 1/2)'
      }));
    } else {
      setAmountErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }

    newIngredients[index] = {
      ...newIngredients[index],
      amount: trimmedValue
    };
    setRecipeData({ ...recipeData, ingredients: newIngredients });
  };

  const addIngredient = () => {
    setRecipeData({
      ...recipeData,
      ingredients: [
        ...recipeData.ingredients,
        { name: '', amount: '1', unit: 'oz', ingredientId: '' }
      ]
    });
  };

  const removeIngredient = (index) => {
    const newIngredients = recipeData.ingredients.filter((_, i) => i !== index);
    setRecipeData({ ...recipeData, ingredients: newIngredients });
  };

  const handleSubmit = async () => {
    try {
      if (!user) {
        setError('You must be logged in to add recipes.');
        return;
      }

      const safeIngredients = recipeData.ingredients.map(ing => ({
        ...ing,
        amount: typeof ing.amount === 'string' ? ing.amount : String(ing.amount || '')
      }));

      const invalidAmounts = safeIngredients.some(ingredient => {
        const amountStr = ingredient.amount || '';
        const trimmedAmount = amountStr.trim();
        return trimmedAmount === '' || parseAmountInput(trimmedAmount) === null;
      });
      if (invalidAmounts) {
        throw new Error(
          'Please enter valid amounts for all ingredients (whole numbers, decimals, or fractions)'
        );
      }

      const formattedIngredients = await Promise.all(
        safeIngredients.map(async ingredient => {
          const ingredientName = ingredient.name.trim();
          if (!ingredientName) {
            throw new Error('Ingredient name cannot be empty');
          }

          const existingIngredient = inventory.find(
            i => i.name.toLowerCase() === ingredientName.toLowerCase() ||
              i.id.toLowerCase() === ingredientName.toLowerCase()
          );
          let ingredientId = existingIngredient?.id || ingredient.ingredientId;

          // Create new ingredient if it doesn't exist
          if (!ingredientId) {
            const newIngredient = {
              name: ingredientName,
              created: new Date().toISOString(),
              updated: new Date().toISOString(),
              userId: user.uid
            };
            const userIngredientsRef = collection(db, `users/${user.uid}/ingredients`);
            const docRef = await addDoc(userIngredientsRef, newIngredient);
            ingredientId = docRef.id;

            if (onIngredientAdded) {
              onIngredientAdded();
            }
          }

          const amountStr = ingredient.amount.trim();
          const parsedAmount = parseAmountInput(amountStr);

          return {
            name: ingredientName,
            amount: `${parsedAmount} ${ingredient.unit}`,
            measurement: parseMeasurement(`${parsedAmount} ${ingredient.unit}`),
            ingredientId
          };
        })
      );

      const recipeToSave = {
        name: recipeData.name,
        origin: 'My Recipes',
        description: recipeData.description,
        ingredients: formattedIngredients,
        instructions: recipeData.instructions,
        garnish: recipeData.garnish,
        glass: recipeData.glass,
        updated: new Date().toISOString(),
        userId: user.uid,
        schemaVersion: 2
      };

      if (isEditMode) {
        const recipeRef = doc(db, `users/${user.uid}/recipes/${recipe.id}`);
        await updateDoc(recipeRef, recipeToSave);
        if (onRecipeUpdated) {
          onRecipeUpdated({ id: recipe.id, ...recipeToSave });
        }
      } else {
        recipeToSave.created = new Date().toISOString();
        const userRecipesRef = collection(db, `users/${user.uid}/recipes`);
        const docRef = await addDoc(userRecipesRef, recipeToSave);
        if (onRecipeAdded) {
          onRecipeAdded({ id: docRef.id, ...recipeToSave });
        }
      }

      setSuccess(`Recipe ${isEditMode ? 'updated' : 'added'} successfully!`);
      setTimeout(() => navigate('/recipes/my-recipes'), 1500);
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} recipe:`, err);
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'add'} recipe. Please try again.`);
    }
  };

  return {
    recipeData,
    setRecipeData,
    error,
    setError,
    success,
    setSuccess,
    amountErrors,
    isEditMode,
    handleIngredientChange,
    handleAmountChange,
    addIngredient,
    removeIngredient,
    handleSubmit,
    parseAmountInput
  };
}; 